import { types as api } from '@mesa-labs/mesa-api';
import React from 'react';
import styled from 'styled-components';

import { formatShortDate } from '../../../../utils';
import {
  ActionItemPartners,
  compareProgramsNewestFirst,
  itemPartnerIds,
  NoPartnerColor,
  partnerColor,
  partnerContextTitle,
  partnerProgramLabel,
} from '../../../../utils/actionItemPartners';

// Partner indicators for Action Item cards, table rows, lane headers and the detail drawer.

// `$nowrap` keeps the chips together so a wrapping parent moves them to the next line as a group.
const Chips = styled.span<{ $nowrap?: boolean }>`
  align-items: center;
  display: inline-flex;
  flex-shrink: ${(p) => (p.$nowrap ? 0 : 1)};
  flex-wrap: ${(p) => (p.$nowrap ? 'nowrap' : 'wrap')};
  gap: 4px;
  min-width: 0;
`;

const Chip = styled.span<{ $color: string; $dashed?: boolean }>`
  background: ${(p) => `${p.$color}14`};
  border: 1px ${(p) => (p.$dashed ? 'dashed' : 'solid')} ${(p) => `${p.$color}66`};
  border-radius: 999px;
  color: ${(p) => p.$color};
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  padding: 0 7px;
  white-space: nowrap;
`;

const PartnerSwatch = styled.span<{ $color: string }>`
  background: ${(p) => p.$color};
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
  height: 8px;
  width: 8px;
`;

// One partner's colored pill; without a partner, a dashed gray pill with `emptyLabel`.
export function PartnerChip({ partnerId, emptyLabel = 'No program' }: {
  readonly partnerId?: api.Partners;
  readonly emptyLabel?: string;
}): React.ReactElement {
  return (
    <Chip $color={partnerColor(partnerId)} $dashed={partnerId === undefined}>
      {partnerId === undefined ? emptyLabel : api.getPartnerName(partnerId)}
    </Chip>
  );
}

// One colored pill per partner the Vendor has a program with, in partner order. Without any program, the onboarding
// partner as a dashed pill, or "No program". Hover lists every program.
export function PartnerChips({ item, nowrap }: {
  readonly item: ActionItemPartners;
  readonly nowrap?: boolean;
}): React.ReactElement {
  const partnerIds = itemPartnerIds(item);
  return (
    <Chips title={partnerContextTitle(item)} $nowrap={nowrap}>
      {partnerIds.length === 0 && (
        <Chip $color={partnerColor(item.onboardingPartnerId)} $dashed>
          {item.onboardingPartnerId ? api.getPartnerName(item.onboardingPartnerId) : 'No program'}
        </Chip>
      )}
      {partnerIds.map((partnerId) => <PartnerChip key={partnerId} partnerId={partnerId} />)}
    </Chips>
  );
}

const ProgramList = styled.span`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 2px;
`;

const ProgramRow = styled.span`
  align-items: center;
  color: #101828;
  display: inline-flex;
  gap: 6px;
`;

const ProgramNote = styled.span`
  color: ${(p) => p.theme.colors.Gray500};
  font-size: 11px;
`;

// Every program the Vendor has, newest (createdAt) first, for the detail drawer: active ones show when they were
// activated, inactive ones (or active without an activation date) when they were added.
export function PartnerProgramList({ item }: { readonly item: ActionItemPartners }): React.ReactElement {
  if (!item.programs?.length) {
    return (
      <ProgramList>
        <ProgramRow>
          <PartnerSwatch $color={NoPartnerColor} />
          {item.onboardingPartnerId ? `No program · onboarded by ${api.getPartnerName(item.onboardingPartnerId)}` : 'No program'}
        </ProgramRow>
      </ProgramList>
    );
  }
  return (
    <ProgramList>
      {[...item.programs].sort(compareProgramsNewestFirst).map((program) => (
        <ProgramRow key={`${program.partnerId}:${program.programCode}`}>
          <PartnerSwatch $color={partnerColor(program.partnerId)} />
          {partnerProgramLabel(program)}
          <ProgramNote>
            {program.isActive && program.activatedAt
              ? `Active · Activated ${formatShortDate(program.activatedAt)}`
              : `${program.isActive ? 'Active' : 'Inactive'} · Added ${formatShortDate(program.createdAt)}`}
          </ProgramNote>
        </ProgramRow>
      ))}
    </ProgramList>
  );
}
